import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  ScrollView, 
  TouchableOpacity,
  Platform,
  Alert
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../../styles/variablesCss';
import { BACKEND_URL, MONTO_MINIMO_ENVIO_GRATIS } from '../../../Config/Const';
import Storage from '../../../utils/Storage';
import { useCheckoutContext } from '../../../context/CheckoutContext';
import { useCart } from '../../../context/CartContext';

const FormaPago = ({ route, mostrarFormasDePagoExterno = false, formaPagoSeleccionadaExterna }) => {
  const [formaPago, setFormaPago] = useState(formaPagoSeleccionadaExterna || ''); // Iniciar con el valor externo si existe
  
  // Estados para planes de pago
  const [tarjetasDisponibles, setTarjetasDisponibles] = useState([]);
  const [tarjetaSeleccionada, setTarjetaSeleccionada] = useState(null);
  const [planesDisponibles, setPlanesDisponibles] = useState([]);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [mostrarPlanes, setMostrarPlanes] = useState(false);
  const {setTarjeta,setCuotas, setCoeficiente , setTotalDeCuota, setTotalFinal, setNroEmision, setNroPlan, setMarcaTarjeta, 
    tipoEntrega, precioFleteDireccion, deseaFactura} = useCheckoutContext();
  const { totales, calcularTotal } = useCart();

  // Observar cambios en la prop externa y cargar planes cuando se muestra externamente
  useEffect(() => {
    // Si se muestra desde FormasDePago y la forma es tarjeta_credito, cargar planes automáticamente
    if (mostrarFormasDePagoExterno && formaPagoSeleccionadaExterna === 'tarjeta_credito') {
      cargarPlanesDePago();
      setMostrarPlanes(true);
    }
  }, [mostrarFormasDePagoExterno]);
  
  // Actualizar el estado interno cuando cambia la prop externa
  useEffect(() => {
    if (formaPagoSeleccionadaExterna) {
      setFormaPago(formaPagoSeleccionadaExterna);
    }
  }, [formaPagoSeleccionadaExterna]);
  
  // Calcular el total dinámicamente desde el carrito y el flete
  const subtotal = totales.subtotal || calcularTotal();
  const descuentoTotal = totales.descuentos || 0;
  const percepciones = totales.percepciones || 0;
  
  // Obtener descuento aplicado de route params si existe
  const { params = {} } = route || {};
  const descuentoAplicado = params.descuentoAplicado || 0;
  
  // Calcular el total de productos (sin flete) para verificar si aplica flete gratis
  // Agregar percepciones solo si el usuario desea factura A o B
  const percepcionesAplicadas = deseaFactura ? percepciones : 0;
  const totalProductos = subtotal - descuentoTotal - descuentoAplicado + percepcionesAplicadas;
  
  // Calcular el flete final
  let fleteAMostrar = 0;
  
  // Si el total de productos es mayor a $100,000, el flete es gratis
  if (totalProductos > MONTO_MINIMO_ENVIO_GRATIS) {
    fleteAMostrar = 0;
  } else {
    // Si no, calcular el flete normalmente
    const fleteCarrito = totales.precioFlete || 0;
    const fleteDireccion = precioFleteDireccion || 0;
    fleteAMostrar = parseInt(tipoEntrega) === 2 
      ? Math.max(fleteCarrito, fleteDireccion) 
      : fleteCarrito;
  }
  
  // Total final incluyendo el flete (base local)
  const totalLocal = totalProductos + fleteAMostrar;
  // Preferir total proveniente de props (con cupón/flete) si existe
  const totalBase = (typeof params.total === 'number' && !Number.isNaN(params.total)) ? params.total : totalLocal;

  useEffect(() => {
    if (formaPago === 'tarjeta_credito') {
      cargarPlanesDePago();
      setMostrarPlanes(true);
    }
  
  }, [formaPago, totalBase, deseaFactura, percepciones]);

  // Efecto para recalcular el plan seleccionado cuando cambia el total (por factura/percepciones)
  useEffect(() => {
    if (planSeleccionado && tarjetaSeleccionada) {
      // Recalcular los valores con el nuevo total base (con cupón/flete si aplica)
      const precioFinal = (totalBase * planSeleccionado.coeficiente).toFixed(2);
      const valorCuota = (precioFinal / planSeleccionado.cuotas).toFixed(2);
      
      setTotalDeCuota(valorCuota);
      setTotalFinal(precioFinal);
    }
  }, [totalBase, planSeleccionado, tarjetaSeleccionada, deseaFactura, percepciones]);

  // Función para cargar los planes de pago
  const cargarPlanesDePago = async () => {
    try {
      let mail = '';
      let codUsu = '';
      
      // Obtener email y codUsu del almacenamiento
      if (Platform.OS === 'web') {
        // En web, usar localStorage
        mail = localStorage.getItem('mail') || '';
        codUsu = localStorage.getItem('codUsu') || '';
      } else {
        // En móvil, usar Storage
        mail = await Storage.getItem('mail') || '';
        codUsu = await Storage.getItem('codUsu') || '';
      }
      
      // Crear query params
      const queryParams = new URLSearchParams();
      if (mail) queryParams.append('mail', mail);
      if (codUsu) queryParams.append('codUsu', codUsu);
      
      
      // Realizar la llamada a la API
      try {
        const response = await fetch(`${BACKEND_URL}/rest/fin/planes?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });
        
        const data = await response.json();
        
        if (data && data.dsResultado && data.dsResultado.tarjetas) {
          setTarjetasDisponibles(data.dsResultado.tarjetas);
        } else {
        }
      } catch (fetchError) {
      }
      return true;
    } catch (error) {
      Alert.alert(
        'Error',
        'No se pudieron cargar los planes de financiación. Por favor, inténtelo nuevamente.',
        [{ text: 'Aceptar' }]
      );
    }
  };
  
  // Formateador de montos en ARS
  const formatMoney = (num) => {
    const n = Number(num) || 0;
    try {
      return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch {
      return n.toFixed(2);
    }
  };
  
  const seleccionarTarjeta = (tarjeta) => {
    setTarjetaSeleccionada(tarjeta);
    setPlanSeleccionado(null);
    
    // Asignar el código correcto de marcaTarjeta según la tarjeta seleccionada
    if (tarjeta) {
      // Establecer el valor adecuado para marcaTarjeta según la marca
      switch(tarjeta.marca) {
        case 'VISA':
          setMarcaTarjeta('V');
          break;
        case 'MASTERCARD':
          setMarcaTarjeta('M');
          break;
        case 'CABAL':
          setMarcaTarjeta('CABAL_ARGENTINA');
          break;
        case 'T. NARANJA':
          setMarcaTarjeta('NARANJA');
          break;
        case 'AMERICAN EXPRESS':
          setMarcaTarjeta('A');
          break;
        default:
          // Si es otra marca, usar el nombre como código
          setMarcaTarjeta(tarjeta.marca);
          break;
      }
      
      // Cargar los planes disponibles
      if (tarjeta.planes) {
        setPlanesDisponibles(tarjeta.planes);
        
        // Auto-seleccionar el plan de 1 cuota por defecto
        const planUnaCuota = tarjeta.planes.find(p => p.cuotas === 1);
        if (planUnaCuota) {
          // Pasar la tarjeta directamente ya que el estado aún no se actualizó
          setTimeout(() => seleccionarPlan(planUnaCuota, tarjeta), 0);
        }
      } else {
        setPlanesDisponibles([]);
      }
    } else {
      setPlanesDisponibles([]);
    }
  };
  
  // Función para seleccionar un plan (tarjetaOverride permite pasar la tarjeta directamente cuando el estado aún no se actualizó)
  const seleccionarPlan = (plan, tarjetaOverride = null) => {
    
    try {
      // Primero actualizar el estado local
      setPlanSeleccionado(plan);
      
      // Usar la tarjeta pasada directamente o la del estado
      const tarjetaActual = tarjetaOverride || tarjetaSeleccionada;
      
      // Enviar datos de financiación al componente padre cuando cambia el plan seleccionado
      if (plan && tarjetaActual) {

        // Calcular precio final usando el plan que se acaba de seleccionar
        const precioFinal = (totalBase * plan.coeficiente).toFixed(2);
        const valorCuota = (precioFinal / plan.cuotas).toFixed(2);
        const emisionNumber = plan.nroEmision !== undefined ? String(plan.nroEmision) : '0';
        
        // Actualizar todos los valores del contexto con el plan nuevo
        setTarjeta(tarjetaActual.marca);
        setCuotas(plan.cuotas); 
        setCoeficiente(plan.coeficiente);
        setNroEmision(emisionNumber);
        setNroPlan(plan.nroPlan);
        setTotalDeCuota(valorCuota);
        setTotalFinal(precioFinal);
      }
    } catch (error) {
    }
  };
  
    return (
      <ScrollView style={styles.container}>
        <View>         
          {formaPago === 'tarjeta_credito' && mostrarPlanes && (
            <View style={styles.planesContainer}>
              <Text style={styles.planesSubtitulo}>Selecciona tu tarjeta</Text>
              
              {Platform.OS === 'web' ? (
                <View style={styles.selectorContainer}>
                  <select 
                    style={styles.selector} 
                    onChange={(e) => {
                      const tarjeta = tarjetasDisponibles.find(
                        t => t.nroEmision === parseInt(e.target.value)
                      );
                      seleccionarTarjeta(tarjeta);
                    }}
                  >
                    <option value="">Selecciona una tarjeta</option>
                    {tarjetasDisponibles.map((tarjeta) => (
                      <option key={tarjeta.nroEmision} value={tarjeta.nroEmision}>
                        {tarjeta.marca}
                      </option>
                    ))}
                  </select>
                </View>
              ) : (
                <View style={styles.selectorContainer}>
                  <TouchableOpacity style={styles.dropdownButton}>
                    <Text>{tarjetaSeleccionada ? tarjetaSeleccionada.marca : 'Selecciona una tarjeta'}</Text>
                    <MaterialCommunityIcons name="chevron-down" size={20} color="#333" />
                  </TouchableOpacity>
                  
                  {/* En una aplicación real, aquí se mostraría un modal o dropdown nativo */}
                </View>
              )}
              
              {tarjetaSeleccionada && (
                <>
                  <Text style={styles.planesSubtitulo}>Selecciona las cuotas</Text>
                  
                  {Platform.OS === 'web' ? (
                    <View style={styles.selectorContainer}>
                      <select 
                        style={styles.selector}
                        value={planSeleccionado?.nroPlan || ''}
                        onChange={(e) => {
                          const selectedValue = parseInt(e.target.value);
                          // Buscar en lista filtrada y original por seguridad
                          const lista = planesDisponibles;
                          const plan = lista.find(p => p.nroPlan === selectedValue);
                          if (plan) {
                            seleccionarPlan(plan);
                          }
                        }}
                      >
                        <option value="">Selecciona las cuotas</option>
                        {(() => {
                          // 1) Dejar solo un plan por cantidad de cuotas, priorizando el menor coeficiente (sin interés si existe)
                          const porCuotas = {};
                          for (const p of (planesDisponibles || [])) {
                            const key = String(p.cuotas);
                            if (!porCuotas[key] || Number(p.coeficiente) < Number(porCuotas[key].coeficiente)) {
                              porCuotas[key] = p;
                            }
                          }
                          const planesFiltrados = Object.values(porCuotas).sort((a, b) => Number(a.cuotas) - Number(b.cuotas));
                          // 2) Render de opciones con formato especial para 100%
                          return planesFiltrados.map((plan) => {
                            const coef = Number(plan.coeficiente);
                            const esSinInteres = Math.abs(coef - 1) < 1e-9;
                            const esPlantaZ = plan.cuotas === 11;
                            const precioFinal = Number(totalBase) * coef;
                            const valorCuotaNum = precioFinal / Number(plan.cuotas);
                            
                            // Si es sin interés, destacar con fondo rojo y texto blanco (sin estrella)
                            if (esSinInteres) {
                              const planText = `Plan Sin Interes`;
                              const detailText = ` (${plan.cuotas}) - $ ${formatMoney(precioFinal)} (${plan.cuotas} de $ ${formatMoney(valorCuotaNum)})`;

                              return (
                                <option
                                  key={plan.nroPlan}
                                  value={plan.nroPlan}
                                  style={{
                                    backgroundColor: '#E21C23',
                                    color: '#FFFFFF',
                                    fontWeight: 'bold',
                                    padding: '8px'
                                  }}
                                >
                                  {`${planText}${detailText}`}
                                </option>
                              );
                            }
                            
                            // Si es PLANTA Z (11 cuotas o más), mostrar sin fondo rojo
                            if (esPlantaZ) {
                              const planText = `PLAN Z`;
                              const detailText = ` (${plan.cuotas}) - $ ${formatMoney(precioFinal)} (${plan.cuotas} de $ ${formatMoney(valorCuotaNum)})`;

                              return (
                                <option
                                  key={plan.nroPlan}
                                  value={plan.nroPlan}
                                >
                                  {`${planText}${detailText}`}
                                </option>
                              );
                            }
                            
                            const label = `${plan.cuotas} - $ ${formatMoney(precioFinal)} (${plan.cuotas} de $ ${formatMoney(valorCuotaNum)})`;
                            return (
                              <option 
                                key={plan.nroPlan} 
                                value={plan.nroPlan}
                              >
                                {label}
                              </option>
                            );
                          });
                        })()}
                      </select>
                    </View>
                  ) : (
                    <View style={styles.selectorContainer}>
                      <TouchableOpacity style={styles.dropdownButton}>
                        <Text>
                          {planSeleccionado 
                            ? `${planSeleccionado.cuotas} cuota${planSeleccionado.cuotas > 1 ? 's' : ''}` 
                            : 'Selecciona las cuotas'}
                        </Text>
                        <MaterialCommunityIcons name="chevron-down" size={20} color="#333" />
                      </TouchableOpacity>
                    </View>
                  )}
                </>
              )}
              
              
            </View>
          )}
        </View>
      </ScrollView>
    );
  

};

// Estilos del componente
const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  planesContainer: {
    padding: 15,
    paddingBottom: 0,
  },
  planesSubtitulo: {
    fontSize: 16,
    fontFamily: fuentes.semiBold,
    marginBottom: 10,
    color: colores.textoPrincipal,
  },
  selectorContainer: {
    marginBottom: 10,
  },
  selector: {
    width: '100%',
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colores.borde,
    fontSize: 16,
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colores.borde,
  },
});

export default FormaPago;
